"use client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import {
    TooltipProvider,
    TooltipTrigger,
    TooltipContent,
    Tooltip,
} from "@/components/ui/tooltip";
import {
    IconAdjustmentsHorizontal,
    IconCheck,
    IconExternalLink,
    IconPaint,
    IconPalette,
    IconQuestionMark,
    IconSelector,
} from "@tabler/icons-react";
import { useState, useEffect } from "react";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ColorPicker } from "./ColorPicker";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    IAccountAssets,
    IBlog,
    ITheme,
    IUserData,
    IUsersBrandAccounts,
} from "@/types";
import { toast } from "sonner";
import { ThemeUpdate } from "@/lib/api";
import React from "react";
import CustomFonts from "@/lib/fonts";
import { CheckStaff } from "@/functions";
import CodeEditor from "@uiw/react-textarea-code-editor";
import { RevalidateLink } from "../layout/RevalidateLink";
import { JoinButton } from "../JoinPro/JoinButton";
import Assets from "../assets";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
interface Props {
    user: IUserData;
    theme: ITheme;
    setTheme: React.Dispatch<React.SetStateAction<ITheme>>;
    assets: IAccountAssets[];
    impersonate?: boolean;
    ws?: WebSocket | null;
    loggedInUser?: IUsersBrandAccounts | undefined;
    blogTheme?: IBlog["theme"];
    setBlogTheme: React.Dispatch<
        React.SetStateAction<IBlog["theme"] | undefined>
    >;
    setIsEditingBlogStyles: React.Dispatch<React.SetStateAction<boolean>>;
    isEditingBlogStyles: boolean;
}
export default function Theme({
    user,
    theme,
    setTheme,
    assets,
    impersonate,
    ws,
    loggedInUser,
    blogTheme,
    setBlogTheme,
    setIsEditingBlogStyles,
    isEditingBlogStyles,
}: Props) {
    const [isExpanded, setIsExpanded] = useState(false);
    useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != "0") {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    const event = new CustomEvent("expandBlock", {
        detail: {
            content: (
                <ThemePopout
                    user={user}
                    theme={theme}
                    setTheme={setTheme}
                    assets={assets}
                    impersonate={impersonate}
                    ws={ws}
                    loggedInUser={loggedInUser}
                    blogTheme={blogTheme}
                    setBlogTheme={setBlogTheme}
                    setIsEditingBlogStyles={setIsEditingBlogStyles}
                    isEditingBlogStyles={isEditingBlogStyles}
                />
            ),
            title: "Theme",
            description:
                "Make your page your own with your own design and colors",
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: "0",
                },
            })
        );
        if (!isExpanded) {
            window.dispatchEvent(event);
            setIsExpanded(true);
        } else {
            window.dispatchEvent(new CustomEvent("collapseBlock"));
            setIsExpanded(false);
        }
    };
    useEffect(() => {
        if (typeof window !== "undefined") {
            const handleMessage = (event: any) => {
                if (event.data.ThemeOpenClose) {
                    handleExpand();
                }
            };
            window.addEventListener("message", handleMessage);
            // Cleanup the event listener on component unmount
            return () => {
                window.removeEventListener("message", handleMessage);
            };
        }
    }, []);
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [theme, blogTheme, isEditingBlogStyles, isExpanded]);
    return (
        <Button
            // size="icon"
            className={`onboarding-theme-button group relative flex h-auto shrink-0 gap-2 rounded-md bg-muted text-sm text-foreground transition-all duration-300 hover:bg-primary hover:text-primary-foreground ${isExpanded ? "bg-primary text-primary-foreground" : "bg-muted"}`}
            onClick={handleExpand}
        >
            <IconPalette size={16} />
            <span className="hidden md:block">Theme</span>
        </Button>
    );
}
const buttonRounded = [
    { number: 1, value: "none" },
    { number: 2, value: "rounded-md" },
    { number: 3, value: "rounded-lg" },
    { number: 4, value: "rounded-xl" },
    { number: 5, value: "rounded-2xl" },
    { number: 6, value: "rounded-full" },
];
export const ThemePopout = ({
    user,
    theme,
    setTheme,
    assets,
    impersonate,
    ws,
    loggedInUser,
    blogTheme,
    setBlogTheme,
    setIsEditingBlogStyles,
    isEditingBlogStyles,
}: Props) => {
    const onThemeChange = async (value: ITheme) => {
        setTheme({
            ...theme,
            ...value,
        });
        if (user.theme === value) return;
        const loader = toast.loading("Updating theme...");
        if (impersonate && ws && loggedInUser?.role !== "user") {
            ws.send(
                JSON.stringify({
                    type: "update_theme",
                    theme: value,
                })
            );
        }
        let response: any = await ThemeUpdate(user, value, impersonate);
        response = await response.json();
        if (!response.error) {
            // Update the user object directly
            toast.success("Updated", { id: loader });
        } else {
            toast.error(
                "Something went wrong, refresh and try again. If the issue persists, please log out and log back in",
                {
                    description: JSON.stringify(response),
                    id: loader,
                }
            );
        }
    };
    const onThemeChangeBlog = async (value: IBlog["theme"]) => {
        setBlogTheme({
            ...blogTheme,
            ...value,
        });
        if (blogTheme === value) return;
        const loader = toast.loading("Updating blog theme...");
        if (impersonate && ws && loggedInUser?.role !== "user") {
            ws.send(
                JSON.stringify({
                    type: "update_blog_theme",
                    theme: value,
                })
            );
        }
        let response: any = await fetch("/api/admin/blog/theme", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                brand_id: impersonate ? user.id : "",
            },
            body: JSON.stringify({
                theme: value,
            }),
        });
        response = await response.json();
        if (!response.error) {
            // Update the user object directly
            toast.success("Updated", { id: loader });
        } else {
            toast.error(
                "Something went wrong, refresh and try again. If the issue persists, please log out and log back in",
                {
                    description: JSON.stringify(response),
                    id: loader,
                }
            );
        }
    };
    const backgroundTab = theme.background_img ? "image" : "color";

    // Blog Styles

    const toggleBlogStylesEdit = (isEditing: boolean) => {
        setIsEditingBlogStyles(isEditing);
        window.dispatchEvent(
            new CustomEvent("toggleBlogStyles", { detail: { isEditing } })
        );
    };
    return (
        <div className="z-10 flex flex-col gap-3 py-3">
            <Tabs defaultValue="general" className="w-full">
                <TabsList className="w-full justify-normal gap-1">
                    <TabsTrigger
                        value="general"
                        onClick={() => toggleBlogStylesEdit(false)}
                        className={`${!isEditingBlogStyles ? "!bg-primary" : "!bg-accent !text-muted-foreground"}`}
                    >
                        General
                    </TabsTrigger>
                    {/*  */}
                    <TabsTrigger
                        value="blog"
                        onClick={() => toggleBlogStylesEdit(true)}
                        className={`${isEditingBlogStyles ? "!bg-primary" : "!bg-accent !text-muted-foreground"}`}
                    >
                        Blog Posts
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="general" className="pt-3">
                    <Accordion type="multiple" className="flex flex-col gap-2">
                        <AccordionItem value="background">
                            <Card className="overflow-hidden border-none">
                                <AccordionTrigger className="rounded-b-md bg-popover px-2 py-3 hover:no-underline">
                                    <CardHeader className="flex flex-row items-center gap-2 p-2">
                                        <IconPaint size={18} />
                                        <CardTitle className="!mt-0 text-sm">
                                            Background
                                        </CardTitle>
                                    </CardHeader>
                                </AccordionTrigger>
                                <AccordionContent className="pb-0 pt-2">
                                    <CardContent className="px-2 pb-2">
                                        <Tabs defaultValue={backgroundTab}>
                                            <TabsList className="mb-2 w-full justify-normal">
                                                <TabsTrigger value="color">
                                                    Color
                                                </TabsTrigger>
                                                <TabsTrigger value="image">
                                                    Image
                                                </TabsTrigger>
                                            </TabsList>
                                            <TabsContent value="color">
                                                <ColorPicker
                                                    value={theme.background}
                                                    gradient={true}
                                                    onChange={function (
                                                        value: string
                                                    ): void {
                                                        onThemeChange({
                                                            ...theme,
                                                            background: value,
                                                            background_img:
                                                                null,
                                                        });
                                                    }}
                                                    dev={CheckStaff(user)}
                                                />
                                            </TabsContent>
                                            <TabsContent value="image">
                                                <Assets
                                                    _assets={assets}
                                                    update={(asset) => {
                                                        if (
                                                            theme.background_img !=
                                                            asset.url
                                                        ) {
                                                            onThemeChange({
                                                                ...theme,
                                                                background_img:
                                                                    asset.url,
                                                            });
                                                        }
                                                    }}
                                                    account={user}
                                                    //@ts-expect-error
                                                    block={{
                                                        url:
                                                            theme.background_img ||
                                                            "",
                                                    }}
                                                    account_id={user.id}
                                                    //@ts-expect-error
                                                    impersonate={
                                                        impersonate
                                                            ? {
                                                                  id: user.id,
                                                              }
                                                            : null
                                                    }
                                                />
                                            </TabsContent>
                                        </Tabs>
                                    </CardContent>
                                </AccordionContent>
                            </Card>
                        </AccordionItem>
                        <AccordionItem value="colors">
                            <Card className="overflow-hidden border-none">
                                <AccordionTrigger className="rounded-b-md bg-popover px-2 py-3 hover:no-underline">
                                    <CardHeader className=" flex flex-row !items-center gap-2 p-2">
                                        <IconPalette size={18} />
                                        <CardTitle className="!mt-0 text-sm">
                                            Colors
                                        </CardTitle>
                                    </CardHeader>
                                </AccordionTrigger>
                                <AccordionContent className="pb-0 pt-2">
                                    <CardContent className="px-2 pb-2">
                                        <div className="flex items-center justify-between py-2">
                                            <div className="flex items-center gap-1">
                                                <Label variant="toggle">
                                                    Heading text
                                                </Label>
                                                <TooltipProvider>
                                                    <Tooltip delayDuration={0}>
                                                        <TooltipTrigger asChild>
                                                            <div className="cursor-pointer rounded-md bg-accent p-1">
                                                                <IconQuestionMark
                                                                    size={14}
                                                                    className=""
                                                                />
                                                            </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent
                                                            className="z-[9999]"
                                                            side="right"
                                                        >
                                                            <p>
                                                                Sets color for
                                                                display name,
                                                                bio, etc
                                                            </p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </div>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        className="h-9 w-9 rounded-full border-2 border-primary"
                                                        style={{
                                                            background:
                                                                theme.text_col_heading,
                                                        }}
                                                    ></Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="z-[999] max-w-full md:w-[26rem]">
                                                    <ColorPicker
                                                        value={
                                                            theme.text_col_heading
                                                        }
                                                        onChange={function (
                                                            value: string
                                                        ): void {
                                                            onThemeChange({
                                                                ...theme,
                                                                text_col_heading:
                                                                    value,
                                                            });
                                                        }}
                                                        dev={CheckStaff(user)}
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                        <Separator />
                                        <div className="flex items-center justify-between py-2">
                                            <div className="flex items-center gap-1">
                                                <Label variant="toggle">
                                                    Block text
                                                </Label>
                                                <TooltipProvider>
                                                    <Tooltip delayDuration={0}>
                                                        <TooltipTrigger asChild>
                                                            <div className="cursor-pointer rounded-md bg-accent p-1">
                                                                <IconQuestionMark
                                                                    size={14}
                                                                    className=""
                                                                />
                                                            </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent
                                                            className="z-[9999]"
                                                            side="right"
                                                        >
                                                            <p>
                                                                Sets color of
                                                                text inside
                                                                blocks
                                                            </p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </div>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        className="h-9 w-9 rounded-full border-2 border-primary"
                                                        style={{
                                                            background:
                                                                theme.text_col_button,
                                                        }}
                                                    ></Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="z-[999] max-w-full md:w-[26rem]">
                                                    <ColorPicker
                                                        value={
                                                            theme.text_col_button
                                                        }
                                                        onChange={function (
                                                            value: string
                                                        ): void {
                                                            onThemeChange({
                                                                ...theme,
                                                                text_col_button:
                                                                    value,
                                                            });
                                                        }}
                                                        dev={CheckStaff(user)}
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                        <Separator />
                                        <div className="flex items-center justify-between py-2">
                                            <div className="flex items-center gap-1">
                                                <Label variant="toggle">
                                                    Block background
                                                </Label>
                                                <TooltipProvider>
                                                    <Tooltip delayDuration={0}>
                                                        <TooltipTrigger asChild>
                                                            <div className="cursor-pointer rounded-md bg-accent p-1">
                                                                <IconQuestionMark
                                                                    size={14}
                                                                    className=""
                                                                />
                                                            </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent
                                                            className="z-[9999]"
                                                            side="right"
                                                        >
                                                            <p>
                                                                Sets color of
                                                                blocks
                                                            </p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </div>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        className="h-9 w-9 rounded-full border-2 border-primary"
                                                        style={{
                                                            background:
                                                                theme.button,
                                                        }}
                                                    ></Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="z-[999] max-w-full md:w-[26rem]">
                                                    <ColorPicker
                                                        value={theme.button}
                                                        gradient={true}
                                                        onChange={function (
                                                            value: string
                                                        ): void {
                                                            onThemeChange({
                                                                ...theme,
                                                                button: value,
                                                            });
                                                        }}
                                                        dev={CheckStaff(user)}
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                        <Separator />
                                        <div className="flex items-center justify-between pt-2">
                                            <div className="flex items-center gap-1">
                                                <Label variant="toggle">
                                                    Accent
                                                </Label>
                                                <TooltipProvider>
                                                    <Tooltip delayDuration={0}>
                                                        <TooltipTrigger asChild>
                                                            <div className="cursor-pointer rounded-md bg-accent p-1">
                                                                <IconQuestionMark
                                                                    size={14}
                                                                    className=""
                                                                />
                                                            </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent
                                                            className="z-[9999]"
                                                            side="right"
                                                        >
                                                            <p>
                                                                Sets color of
                                                                the navigation
                                                                bar
                                                            </p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </div>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        className="h-9 w-9 rounded-full border-2 border-primary"
                                                        style={{
                                                            background:
                                                                theme.accent_col,
                                                        }}
                                                    ></Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="z-[999] max-w-full md:w-[26rem]">
                                                    <ColorPicker
                                                        value={theme.accent_col}
                                                        onChange={function (
                                                            value: string
                                                        ): void {
                                                            onThemeChange({
                                                                ...theme,
                                                                accent_col:
                                                                    value,
                                                            });
                                                        }}
                                                        dev={CheckStaff(user)}
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                    </CardContent>
                                </AccordionContent>
                            </Card>
                        </AccordionItem>
                        <AccordionItem value="style">
                            <Card className="overflow-hidden border-none">
                                <AccordionTrigger className="rounded-b-md bg-popover px-2 py-3 hover:no-underline">
                                    <CardHeader className="flex flex-row items-center gap-2 p-2">
                                        <IconAdjustmentsHorizontal size={18} />
                                        <CardTitle className="!mt-0 text-sm">
                                            Customization
                                        </CardTitle>
                                    </CardHeader>
                                </AccordionTrigger>
                                <AccordionContent className="pb-0 pt-2">
                                    <CardContent className="flex flex-col gap-3 px-2 pb-2">
                                        <div className="flex flex-col gap-1 pt-2">
                                            <Label variant="toggle">
                                                Rounded
                                            </Label>
                                            <Slider
                                                step={1}
                                                min={1}
                                                max={6}
                                                value={[
                                                    buttonRounded.find(
                                                        (e) =>
                                                            e.value ==
                                                            theme.button_rounded
                                                    )?.number || 1,
                                                ]}
                                                onValueChange={function (
                                                    value: any
                                                ): void {
                                                    onThemeChange({
                                                        ...theme,
                                                        button_rounded:
                                                            buttonRounded.find(
                                                                (e) =>
                                                                    e.number ==
                                                                    value[0]
                                                            )?.value || "none",
                                                    });
                                                }}
                                            />
                                        </div>
                                        <Separator />
                                        <div className="flex flex-col gap-1 pt-2">
                                            <Label variant="toggle">
                                                Inset
                                            </Label>
                                            <div className="flex items-center justify-between gap-6">
                                                <div
                                                    className={`border-navigationAccent h-12 w-1/2 cursor-pointer rounded-md border-2 bg-muted hover:border-primary ${
                                                        theme.inset
                                                            ? ""
                                                            : "border-primary"
                                                    }`}
                                                    onClick={() => {
                                                        if (theme.inset) {
                                                            onThemeChange({
                                                                ...theme,
                                                                inset: false,
                                                            });
                                                        }
                                                    }}
                                                />
                                                <div
                                                    className={`border-navigationAccent h-12 w-1/2 cursor-pointer rounded-md border-2 bg-transparent hover:border-primary ${
                                                        theme.inset
                                                            ? "border-primary"
                                                            : ""
                                                    }`}
                                                    onClick={() => {
                                                        if (!theme.inset) {
                                                            onThemeChange({
                                                                ...theme,
                                                                inset: true,
                                                            });
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <Separator />
                                        <div className="flex flex-col gap-1 pt-2">
                                            <Label variant="toggle">
                                                Shadow
                                            </Label>
                                            <Slider
                                                step={25}
                                                max={100}
                                                min={0}
                                                value={[
                                                    theme.button_shadow || 0,
                                                ]}
                                                onValueChange={function (
                                                    value: any
                                                ): void {
                                                    onThemeChange({
                                                        ...theme,
                                                        button_shadow: value[0],
                                                    });
                                                }}
                                            />
                                        </div>

                                        <Separator />
                                        <div className="flex flex-col gap-1 pt-2">
                                            <Label variant="toggle">Font</Label>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        role="combobox"
                                                        className="w-full justify-between bg-muted transition-all duration-300 hover:bg-accent"
                                                        style={{
                                                            fontFamily:
                                                                theme.font
                                                                    ?.font,
                                                        }}
                                                    >
                                                        {theme.font?.font
                                                            ? CustomFonts.find(
                                                                  (font) =>
                                                                      font.font ===
                                                                      theme.font
                                                                          ?.font
                                                              )?.name
                                                            : "Select font..."}
                                                        <IconSelector className="opacity-50" />
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-full min-w-full p-0">
                                                    <Command className="max-h-96 min-w-full">
                                                        <CommandInput
                                                            placeholder="Search fonts..."
                                                            className="h-9 placeholder:text-muted-foreground"
                                                        />
                                                        <CommandList>
                                                            <CommandEmpty>
                                                                No font found.
                                                            </CommandEmpty>
                                                            <CommandGroup>
                                                                {CustomFonts.sort(
                                                                    (a, b) =>
                                                                        a.name.localeCompare(
                                                                            b.name
                                                                        )
                                                                ).map(
                                                                    (
                                                                        font,
                                                                        index
                                                                    ) => (
                                                                        <CommandItem
                                                                            key={
                                                                                index
                                                                            }
                                                                            value={
                                                                                font.font
                                                                            }
                                                                            onSelect={(
                                                                                value
                                                                            ) => {
                                                                                onThemeChange(
                                                                                    {
                                                                                        ...theme,
                                                                                        font: {
                                                                                            font: value,
                                                                                            family: "",
                                                                                        },
                                                                                    }
                                                                                );
                                                                            }}
                                                                        >
                                                                            <span
                                                                                style={{
                                                                                    fontFamily:
                                                                                        font.font,
                                                                                }}
                                                                            >
                                                                                {
                                                                                    font.name
                                                                                }
                                                                            </span>
                                                                            <IconCheck
                                                                                className={cn(
                                                                                    "ml-auto",
                                                                                    theme
                                                                                        .font
                                                                                        ?.font ===
                                                                                        font.font
                                                                                        ? "opacity-100"
                                                                                        : "opacity-0"
                                                                                )}
                                                                            />
                                                                        </CommandItem>
                                                                    )
                                                                )}
                                                            </CommandGroup>
                                                        </CommandList>
                                                    </Command>
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                        <Separator />
                                        <div className="relative flex flex-col gap-1 pt-2">
                                            <Label variant="toggle">
                                                Custom CSS
                                            </Label>
                                            <Label
                                                variant="subtoggle"
                                                className="flex items-center
                        "
                                            >
                                                <a
                                                    href="/documentation/custom-css"
                                                    target="_blank"
                                                >
                                                    Documentation
                                                </a>
                                                <IconExternalLink size={14} />
                                            </Label>
                                            <div className="relative h-full">
                                                <CodeEditor
                                                    disabled={
                                                        user.account_type !==
                                                            "premium" &&
                                                        user.account_type !==
                                                            "bplus" &&
                                                        user.account_type !==
                                                            "bpro"
                                                    }
                                                    value={theme.css}
                                                    language="css"
                                                    placeholder=".block {background-color: #4CAF50; border: none;}"
                                                    onChange={(evn: any) => {
                                                        setTheme({
                                                            ...theme,
                                                            css: evn.target
                                                                .value,
                                                        });
                                                    }}
                                                    onBlur={(evn: any) => {
                                                        onThemeChange({
                                                            ...theme,
                                                            css: evn.target
                                                                .value,
                                                        });
                                                    }}
                                                    padding={15}
                                                    style={{
                                                        backgroundColor:
                                                            "transparent",
                                                        width: "100%",
                                                        borderRadius: 8,
                                                        border: "2px solid #403E3E",
                                                        fontFamily:
                                                            "ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
                                                    }}
                                                    className={`!text-foreground ${user.account_type !== "premium" && user.account_type !== "bplus" && user.account_type !== "bpro" ? " blur-sm" : ""}`}
                                                />
                                                {user.account_type ==
                                                    "user" && (
                                                    // <DorasProBlur user={user} />
                                                    <Card className="my-3 border-primary bg-primary/30">
                                                        <CardHeader className="pt-3">
                                                            <CardTitle>
                                                                Upgrade to Doras
                                                                Pro
                                                            </CardTitle>
                                                            <CardDescription>
                                                                Get started for
                                                                only $3.99/month
                                                                to access custom
                                                                CSS and so much
                                                                more
                                                            </CardDescription>
                                                            <CardContent className="px-0 pb-0">
                                                                <JoinButton />
                                                            </CardContent>
                                                        </CardHeader>
                                                    </Card>
                                                )}
                                                {user.account_type ==
                                                    "bfree" && (
                                                    // <DorasProBlur
                                                    //     user={user}
                                                    //     title="Upgrade for more"
                                                    //     description="Free brand accounts do not have access to custom CSS. Upgrade to Brands Plus or Pro to unlcock this and so much more"
                                                    //     brandButton={
                                                    //         <RevalidateLink
                                                    //             href={`/admin/brand/${user.id}/settings/billing`}
                                                    //         >
                                                    //             <Button>Learn More</Button>
                                                    //         </RevalidateLink>
                                                    //     }
                                                    // />
                                                    <Card className="my-3 border-primary bg-primary/30">
                                                        <CardHeader className="pt-3">
                                                            <CardTitle>
                                                                Upgrade for more
                                                            </CardTitle>
                                                            <CardDescription>
                                                                Free brand
                                                                accounts do not
                                                                have access to
                                                                custom CSS.
                                                                Upgrade to
                                                                Brands Plus or
                                                                Pro to unlcock
                                                                this and so much
                                                                more
                                                            </CardDescription>
                                                            <CardContent className="px-0 pb-0">
                                                                <RevalidateLink
                                                                    href={`/admin/brand/${user.id}/settings/billing`}
                                                                >
                                                                    <Button>
                                                                        Learn
                                                                        More
                                                                    </Button>
                                                                </RevalidateLink>
                                                            </CardContent>
                                                        </CardHeader>
                                                    </Card>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </AccordionContent>
                            </Card>
                        </AccordionItem>
                    </Accordion>
                </TabsContent>
                <TabsContent value="blog" className="pt-3">
                    <Accordion
                        type="single"
                        collapsible
                        className="flex flex-col gap-2"
                    >
                        <AccordionItem value="background">
                            <Card>
                                <AccordionTrigger className="pr-2 hover:no-underline">
                                    <CardHeader className="flex flex-row items-center gap-2 p-2">
                                        <IconPaint />
                                        <CardTitle className="!mt-0">
                                            Background
                                        </CardTitle>
                                    </CardHeader>
                                </AccordionTrigger>
                                <AccordionContent className="pb-0">
                                    <CardContent className="px-2 pb-2">
                                        <ColorPicker
                                            value={blogTheme?.background || ""}
                                            gradient={true}
                                            onChange={function (
                                                value: string
                                            ): void {
                                                blogTheme &&
                                                    onThemeChangeBlog({
                                                        ...blogTheme,
                                                        background: value,
                                                        background_img: null,
                                                    });
                                            }}
                                            dev={CheckStaff(user)}
                                        />
                                    </CardContent>
                                </AccordionContent>
                            </Card>
                        </AccordionItem>
                        <AccordionItem value="colors">
                            <Card>
                                <AccordionTrigger className="pr-2 hover:no-underline">
                                    <CardHeader className=" flex flex-row !items-center gap-2 p-2">
                                        <IconPalette />
                                        <CardTitle className="!mt-0">
                                            Colors
                                        </CardTitle>
                                    </CardHeader>
                                </AccordionTrigger>
                                <AccordionContent className="pb-0">
                                    <CardContent className="px-2 pb-2">
                                        <div className="flex items-center justify-between py-2">
                                            <div className="flex items-center gap-1">
                                                <Label variant="toggle">
                                                    Heading text
                                                </Label>
                                                <TooltipProvider>
                                                    <Tooltip delayDuration={0}>
                                                        <TooltipTrigger asChild>
                                                            <div className="cursor-pointer rounded-md bg-accent p-1">
                                                                <IconQuestionMark
                                                                    size={14}
                                                                    className=""
                                                                />
                                                            </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent
                                                            className="z-[9999]"
                                                            side="right"
                                                        >
                                                            <p>
                                                                Sets color for
                                                                blog title,
                                                                author and post
                                                                date
                                                            </p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </div>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        className="h-9 w-9 rounded-full border-2 border-primary"
                                                        style={{
                                                            background:
                                                                blogTheme?.heading_text,
                                                        }}
                                                    ></Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="z-[999] max-w-full md:w-[26rem]">
                                                    <ColorPicker
                                                        value={
                                                            blogTheme?.heading_text ||
                                                            ""
                                                        }
                                                        onChange={function (
                                                            value: string
                                                        ): void {
                                                            blogTheme &&
                                                                onThemeChangeBlog(
                                                                    {
                                                                        ...blogTheme,
                                                                        heading_text:
                                                                            value,
                                                                    }
                                                                );
                                                        }}
                                                        dev={CheckStaff(user)}
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                        <Separator />
                                        <div className="flex items-center justify-between py-2">
                                            <div className="flex items-center gap-1">
                                                <Label variant="toggle">
                                                    Button text
                                                </Label>
                                                <TooltipProvider>
                                                    <Tooltip delayDuration={0}>
                                                        <TooltipTrigger asChild>
                                                            <div className="cursor-pointer rounded-md bg-accent p-1">
                                                                <IconQuestionMark
                                                                    size={14}
                                                                    className=""
                                                                />
                                                            </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent
                                                            className="z-[9999]"
                                                            side="right"
                                                        >
                                                            <p>
                                                                Sets color of
                                                                text inside
                                                                buttons like
                                                                "Home" & "My
                                                                Profile"
                                                            </p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </div>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        className="h-9 w-9 rounded-full border-2 border-primary"
                                                        style={{
                                                            background:
                                                                blogTheme?.button_text,
                                                        }}
                                                    ></Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="z-[999] max-w-full md:w-[26rem]">
                                                    <ColorPicker
                                                        value={
                                                            blogTheme?.button_text ||
                                                            ""
                                                        }
                                                        onChange={function (
                                                            value: string
                                                        ): void {
                                                            blogTheme &&
                                                                onThemeChangeBlog(
                                                                    {
                                                                        ...blogTheme,
                                                                        button_text:
                                                                            value,
                                                                    }
                                                                );
                                                        }}
                                                        dev={CheckStaff(user)}
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                        <Separator />
                                        <div className="flex items-center justify-between py-2">
                                            <div className="flex items-center gap-1">
                                                <Label variant="toggle">
                                                    Button
                                                </Label>
                                                <TooltipProvider>
                                                    <Tooltip delayDuration={0}>
                                                        <TooltipTrigger asChild>
                                                            <div className="cursor-pointer rounded-md bg-accent p-1">
                                                                <IconQuestionMark
                                                                    size={14}
                                                                    className=""
                                                                />
                                                            </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent
                                                            className="z-[9999]"
                                                            side="right"
                                                        >
                                                            <p>
                                                                Sets color of
                                                                the "Home" and
                                                                "My Profile"
                                                                buttons
                                                            </p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </div>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        className="h-9 w-9 rounded-full border-2 border-primary"
                                                        style={{
                                                            background:
                                                                blogTheme?.button_bg,
                                                        }}
                                                    ></Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="z-[999] max-w-full md:w-[26rem]">
                                                    <ColorPicker
                                                        value={
                                                            blogTheme?.button_bg ||
                                                            ""
                                                        }
                                                        gradient={true}
                                                        onChange={function (
                                                            value: string
                                                        ): void {
                                                            blogTheme &&
                                                                onThemeChangeBlog(
                                                                    {
                                                                        ...blogTheme,
                                                                        button_bg:
                                                                            value,
                                                                    }
                                                                );
                                                        }}
                                                        dev={CheckStaff(user)}
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                    </CardContent>
                                </AccordionContent>
                            </Card>
                        </AccordionItem>
                    </Accordion>
                </TabsContent>
            </Tabs>
        </div>
    );
};
